import { Box } from '@mui/material';
import { Suspense, useEffect } from 'react';
import { DataGridViewType } from '@/features/DataGrid';
import { useDataFetcher } from '@/shared/hooks';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Loader } from '@/shared/ui';
import {
  setupColumnsMeta,
  selectMetaIsLoaded,
  selectMetaConfig,
} from '../../model/slice';
import type { DataConfig } from '../../model/types';
import { useDataFetchersContext } from '../DataFetchersContext';
import { DataTable } from '../DataTable/DataTable';
import { TreeDataGrid } from '../TreeDataGrid/TreeDataGrid';

interface Props {
  preset: unknown;
  isReadonly?: boolean;
  onReorderDimensions?: (info: {
    id: string;
    oldIndex: number;
    newIndex: number;
  }) => void;
  onColumnsWidthChange?: (columnName: string, newWidth?: number) => void;
}

export function CohortRetentionWidget({
  preset,
  isReadonly,
  onReorderDimensions,
  onColumnsWidthChange,
}: Props) {
  const dispatch = useAppDispatch();
  const config = preset as DataConfig;
  const metaIsLoaded = useAppSelector(selectMetaIsLoaded);
  const metaColumnsConfig = useAppSelector(selectMetaConfig);
  const { onGetMetricsMinMax } = useDataFetchersContext();
  const { data: minMax } = useDataFetcher(onGetMetricsMinMax);

  useEffect(() => {
    dispatch(setupColumnsMeta());
  }, [dispatch]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
      }}
    >
      <Loader active={!metaIsLoaded} />
      {metaIsLoaded && (
        <Suspense fallback={<Loader active />}>
          {config.dataGridViewType === DataGridViewType.treeDataGrid ? (
            <TreeDataGrid
              isReadonly={isReadonly}
              rowHeight={config.rowHeight}
              columnsWidth={config.columnsWidth}
              columnsConfig={metaColumnsConfig}
              dimensions={config.dimensions}
              metric={config.metric}
              minMax={minMax}
              updateColumnsWidth={isReadonly ? undefined : onColumnsWidthChange}
            />
          ) : (
            <DataTable
              isReadonly={isReadonly}
              rowHeight={config.rowHeight}
              columnsWidth={config.columnsWidth}
              columnsConfig={metaColumnsConfig}
              dimensions={config.dimensions}
              metric={config.metric}
              minMax={minMax}
              reorderDimensions={isReadonly ? undefined : onReorderDimensions}
              updateColumnsWidth={isReadonly ? undefined : onColumnsWidthChange}
            />
          )}
        </Suspense>
      )}
    </Box>
  );
}
